import { mdiBattery, mdiBattery10, mdiBattery50, mdiBatteryUnknown } from "@mdi/js";
import { BATTERY_MODELS, DEVICE_TYPES as T, PRESENCE_TYPES, modelOf, typeOf } from "../../constants/devices";

const isSet = (value) => value !== undefined && value !== null && value !== "";

// The sensor kind of a device (a DEVICE_TYPES value).
export const sensorType = (device) => typeOf(device);

// The flag the app's icon table uses: door sensors report isClosed, door bells
// isTriggered, and motion, gas and smoke sensors isPresent.
export const sensorIconFlag = (deviceType, props = {}) => {
  if (deviceType === T.DW_SENSOR) {
    return props.isClosed ?? false;
  }
  if (deviceType === T.DOOR_BELL) {
    return props.isTriggered ?? false;
  }
  if (PRESENCE_TYPES.includes(deviceType)) {
    return props.isPresent ?? false;
  }
  return props.state ?? false;
};

// Readable state from a sensor's last-state props, used for labels and screen readers.
export const sensorState = (deviceType, props = {}) => {
  if (deviceType === T.DW_SENSOR && isSet(props.isClosed)) {
    return { label: props.isClosed ? "Closed" : "Open", alert: false };
  }
  if (deviceType === T.DOOR_BELL && isSet(props.isTriggered)) {
    return { label: props.isTriggered ? "Triggered" : "Not triggered", alert: false };
  }
  if (PRESENCE_TYPES.includes(deviceType) && isSet(props.isPresent)) {
    return {
      label: props.isPresent ? "Detected" : "Not detected",
      alert: Boolean(props.isPresent) && deviceType !== T.PIR_SENSOR,
    };
  }
  return null;
};

// Laststate sends battery_status (0 low, 1 medium, 2 full); the app renames it
// batteryStatus. Only some models report a battery.
export const batteryLevel = (props = {}, device) => {
  if (device && !BATTERY_MODELS.includes(modelOf(device))) return null;
  const status = props.batteryStatus ?? props.battery_status;
  const levels = [
    { label: "Low", path: mdiBattery10 },
    { label: "Medium", path: mdiBattery50 },
    { label: "Full", path: mdiBattery },
  ];
  if (!isSet(status)) return device ? { label: "Unknown", path: mdiBatteryUnknown } : null;
  return levels[Number(status)] ?? { label: "Unknown", path: mdiBatteryUnknown };
};

// WFSL_1 alarm codes, reported in history entries with status_id 8
// (app/screens/CommonSwichDetails.js).
const LOCK_ALARMS = {
  0: "Fingerprint mismatch",
  1: "Incorrect code",
  2: "Incorrect card",
  3: "Incorrect card",
  4: "Incorrect code",
  5: "Device hijack",
  6: "Device hijack",
  7: "Device hijack",
  8: "Device hijack",
  9: "Device hijack",
  10: "Low battery level",
  11: "Low battery power off",
  12: "Device hijack",
  13: "Door bell ring",
};

// History entry label for sensors, or null for other device types. The entry's
// status is the armed flag, as in the mobile app.
export const sensorHistoryLabel = (deviceType, entry) => {
  const info = entry?.info ?? {};
  let event;
  if (deviceType === T.DW_SENSOR) {
    event = info.isClosed ? "Closed" : "Open";
  } else if (PRESENCE_TYPES.includes(deviceType)) {
    event = info.isPresent ? "Detected" : "Not detected";
  } else if (deviceType === T.DOOR_BELL) {
    event = info.isTriggered ? "Triggered" : "Not triggered";
  } else if (deviceType === T.SMART_LOCK) {
    event = info.status_id === 8 ? `Alarm: ${LOCK_ALARMS[info.status] ?? "Alarm"}` : "Event";
  } else {
    return null;
  }
  return `${event} (${entry?.status ? "Armed" : "Unarmed"})`;
};
